import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { sdk } from "@/api/sdk";
import { clientQueryOf, pickMembership, readStoredClientId } from "@/features/booking/hooks/use-booking-client";
import { ClientSpecialist } from "@/features/booking/modules/client-specialist/client-specialist";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/(private)/book/$specialistId/")({
  // The breadcrumb names the specialist. The same requests feed the page, so this warms its cache.
  loader: async ({ params, location }) => {
    const me = await sdk.bookingClient.me.$get.send();
    const org = new URLSearchParams(location.searchStr).get("org");
    const membership =
      me.data?.find((candidate) => candidate.organizationId === org) ??
      pickMembership(me.data ?? [], readStoredClientId());
    if (!membership) return { crumb: m.booking_portal_specialist_crumb() };
    const { data } = await sdk.bookingClient.specialists.$specialistId.$get
      .setParams({ specialistId: params.specialistId })
      .setQueryParams(clientQueryOf(membership))
      .send();
    return { crumb: data?.specialist.name ?? m.booking_portal_specialist_crumb() };
  },
  // `org` is the trainer this specialist works for, from the Book list or a public page's link.
  // Somebody who is not their client yet is signed up there on arrival.
  validateSearch: z.object({
    serviceId: z.string().optional(),
    startAt: z.string().optional(),
    org: z.string().optional(),
  }),
  component: SpecialistPage,
});

function SpecialistPage() {
  const { specialistId } = Route.useParams();
  const { serviceId, startAt } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <ClientSpecialist
      key={specialistId}
      specialistId={specialistId}
      serviceId={serviceId}
      startAt={startAt}
      onBack={() => void navigate({ to: "/book" })}
      onSeeBookings={(bookingId) => void navigate({ to: "/my-bookings", search: { bookingId } })}
    />
  );
}
